import { createClient } from '@connectrpc/connect';
import { RfiEvidenceService } from '../../../../../gen/ts/mono/projectdata/v1/rfi_evidence_connect';
import { RfiSectionService } from '../../../../../gen/ts/mono/projectdata/v1/rfi_sections_connect';
import { agentClient, artifactClient, rfiQuestionsClient } from '../../lib/connect/clients';
import { connectTransport } from '../../lib/connect/transport';
import { libraryReleaseClient } from '../../pages/Library/reuseClient';
const rfiSectionClient = createClient(RfiSectionService, connectTransport);
export const evidenceClient = createClient(RfiEvidenceService, connectTransport);
export function downloadDocx(content, name) {
    const url = URL.createObjectURL(new Blob([
        content.slice().buffer
    ], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    }));
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.click();
    setTimeout(()=>URL.revokeObjectURL(url), 1000);
}
/** AI decisions deliberately have no dependency on the manual-only DecideRfiDraft RPC. */ export function createRfiEvidenceApi(deps = {
    evidence: evidenceClient,
    agent: agentClient,
    artifacts: artifactClient,
    library: libraryReleaseClient,
    questions: rfiQuestionsClient,
    sections: rfiSectionClient
}) {
    return {
        async options (target) {
            const [library, artifacts] = await Promise.all([
                deps.library.listSelectableReleases({}),
                deps.artifacts.listEntityArtifacts({
                    entityType: 'rfi_response_project',
                    entityId: target.rfiResponseProjectId
                })
            ]);
            return [
                ...library.releases.map((r)=>({
                        key: `release:${r.releaseId}`,
                        title: `${r.releasedTitle} · Library v${r.version} · ${r.releaseContext?.itemType || 'evidence'}`,
                        source: {
                            kind: 'library_release',
                            libraryItemId: r.libraryItemId,
                            releaseId: r.releaseId,
                            releaseVersion: r.version
                        }
                    })),
                ...artifacts.artifacts.filter((a)=>a.version > 0 && a.checksumSha256).map((a)=>({
                        key: `artifact:${a.artifactId}:${a.version}`,
                        title: `${a.filename} · artifact v${a.version} · external eligibility unverified`,
                        source: {
                            kind: 'artifact',
                            artifactId: a.artifactId,
                            artifactVersion: a.version,
                            sha256: a.checksumSha256
                        }
                    }))
            ];
        },
        questions: (target)=>deps.questions.listRfiQuestions(target),
        state: async (target)=>(await deps.evidence.getRfiEvidenceState(target)).state,
        resolve: async (target, questionId, source)=>(await deps.evidence.resolveRfiEvidenceContext({
                ...target,
                questionId,
                source
            })).context,
        async generate (target, questionId, sources, intent) {
            const response = await deps.agent.generateRfiDraft({
                ...target,
                questionId,
                sources,
                intent
            });
            if (!response.draft || !response.conversationId || !response.toolCallId || response.draft.conversationId !== response.conversationId || response.draft.toolCallId !== response.toolCallId) throw new Error('Saved proposal identity unavailable. Reopen review.');
            const proposal = response.proposal?.toJson();
            const metadata = proposal?.rfiProposal;
            return {
                draft: response.draft,
                questionText: metadata?.questionText || '',
                preview: response.previewDocx,
                status: 'pending'
            };
        },
        async restore (draft) {
            const response = await deps.agent.getConversation({
                id: draft.conversationId
            });
            if (response.readOnly) throw new Error('This saved proposal is read-only.');
            for (const message of response.messages){
                const parts = message.parts?.toJson();
                if (!Array.isArray(parts)) continue;
                for (const part of parts){
                    const p = part;
                    if (p.type !== 'tool-result' || p.toolCallId !== draft.toolCallId || p.result?._rfiDraft !== true) continue;
                    const metadata = p.result.rfiProposal;
                    const stored = p.result.rfiDecision;
                    let intent;
                    if (stored && [
                        'accepted',
                        'edited',
                        'rejected'
                    ].includes(stored.decision || '')) {
                        intent = {
                            decision: stored.decision
                        };
                        if (intent.decision === 'edited') intent.reviewedContent = Uint8Array.from(atob(stored.content || ''), (c)=>c.charCodeAt(0));
                    }
                    return {
                        draft,
                        questionText: metadata?.questionText || '',
                        status: String(p.result.approvalState),
                        intent
                    };
                }
            }
            throw new Error('Owned saved proposal unavailable. Reopen review.');
        },
        decide: (review, decision)=>deps.agent.decideDocumentProposal({
                conversationId: review.draft.conversationId,
                toolCallId: review.draft.toolCallId,
                ...decision
            }),
        followUp: (target, title)=>deps.sections.createRfiFollowUp({
                ...target,
                title
            }),
        async outwardCurrent (target) {
            const state = (await deps.evidence.getRfiEvidenceState(target)).state;
            const current = state?.history.find((h)=>h.currentAnswer && h.outwardEligible && !h.evidenceWithheld && !h.sourceWithdrawn);
            if (!current?.draft?.id) throw new Error('No current reviewed answer is eligible for outward output. Review the evidence and saved answer first.');
            const result = await deps.evidence.renderRfiOutward({
                ...target,
                draftId: current.draft.id
            });
            if (result.exclusionReason || !result.publication || !result.content.length) throw new Error(result.exclusionReason || 'The reviewed answer is no longer eligible for outward output.');
            return result.content;
        },
        outward: async (target, draftId)=>{
            const result = await deps.evidence.renderRfiOutward({
                ...target,
                draftId
            });
            if (result.exclusionReason || !result.publication || !result.content.length) throw new Error(result.exclusionReason || 'No eligible reviewed answer is available for export.');
            return result.content;
        }
    };
}
export const rfiEvidenceApi = createRfiEvidenceApi();
